/**
 * MarketStats — packaged, self-contained market analytics component.
 *
 * v2 architecture:
 *   - Header strip: title, scope picker, window pills, close
 *   - Tab bar: 5 tabs (Overview, Velocity, Pricing, STR, Long-term)
 *   - Tab body: lazy-rendered per tab; data fetched on first activation,
 *     cached in this.tabPayloads so tab-switching is instant after load
 *
 * Modes:
 *   - Live: pass dataApiUrl; component fetches each tab's data as needed
 *   - Snapshot: pass initialData (Overview-tab payload only) for SEO embeds;
 *     other tabs still fetch on click if dataApiUrl is also provided
 *
 * Drill-through: clicking an interactive chart calls onDrillToListings
 * with the scope + any narrowing filters; the host typically closes the
 * modal and applies filters to the listings drawer.
 */
import { renderSaleToListHistogram, renderStrPremium, renderPriceDistribution, renderAbsorptionFull, renderBucketedHistogram, renderBedroomGrid, renderSaleToListTrend, renderStrPerDistrict, renderStrComparison, renderAnnualPriceTrend, renderAnnualVolume, renderHoldingPeriod, attachHoverTooltip, } from './charts.js';
import { MARKET_STATS_STYLES } from './styles.js';
import { Disclaimers } from '../PropertyModal/Disclaimers.js';
const WINDOW_OPTIONS = [
    { key: '30d', label: '30d' },
    { key: '90d', label: '90d' },
    { key: '12mo', label: '12mo' },
];
const TAB_OPTIONS = [
    { key: 'overview', label: 'Overview' },
    { key: 'velocity', label: 'Velocity' },
    { key: 'pricing', label: 'Pricing' },
    { key: 'str', label: 'STR' },
    { key: 'long-term', label: 'Long-term' },
];
export class MarketStats {
    opts;
    container;
    districts;
    window;
    activeTab;
    // Per-tab payload cache. Switching scope/window invalidates everything.
    overview = { status: 'pending' };
    velocity = { status: 'pending' };
    pricing = { status: 'pending' };
    strTab = { status: 'pending' };
    longTerm = { status: 'pending' };
    // The Long-term tab has its own horizon control (5y/10y/20y) rather than
    // sharing the global window. Tracked here.
    longTermYears = 5;
    // Increment-on-fetch sentinel so out-of-order responses are dropped (e.g.,
    // user clicks a different scope before the previous fetch returns).
    fetchSeq = 0;
    disclaimers = null;
    constructor(opts) {
        this.opts = opts;
        this.container = opts.container;
        this.districts = opts.initialDistricts ?? [];
        this.window = opts.initialWindow ?? '90d';
        this.activeTab = opts.initialTab ?? 'overview';
        if (opts.initialData) {
            this.overview = { status: 'loaded', data: opts.initialData };
        }
    }
    async mount() {
        this.container.innerHTML = `<style>${MARKET_STATS_STYLES}</style><div class="ms-root" id="ms-root"></div>`;
        // Render the shell immediately so the user sees structure even before
        // the first fetch returns. The active tab triggers its own fetch.
        this.renderShell();
        await this.ensureTabLoaded(this.activeTab);
        this.renderActiveTab();
    }
    setScope(districts) {
        this.districts = districts;
        this.invalidateAllTabs();
        this.renderShell();
        void this.ensureTabLoaded(this.activeTab).then(() => this.renderActiveTab());
    }
    setWindow(w) {
        this.window = w;
        this.invalidateAllTabs();
        this.renderShell();
        void this.ensureTabLoaded(this.activeTab).then(() => this.renderActiveTab());
    }
    setTab(tab) {
        if (tab === this.activeTab)
            return;
        this.activeTab = tab;
        this.renderShell();
        void this.ensureTabLoaded(tab).then(() => this.renderActiveTab());
    }
    destroy() {
        this.disclaimers?.destroy();
        this.disclaimers = null;
        this.container.innerHTML = '';
    }
    // ---------------------------------------------------------------------
    // Fetch coordination
    // ---------------------------------------------------------------------
    invalidateAllTabs() {
        this.overview = { status: 'pending' };
        this.velocity = { status: 'pending' };
        this.pricing = { status: 'pending' };
        this.strTab = { status: 'pending' };
        this.longTerm = { status: 'pending' };
    }
    async ensureTabLoaded(tab) {
        // Pricing/STR/Velocity/Long-term each have their own per-tab payload.
        // Overview is the base payload and is also still used as a fallback
        // for the Pricing/STR tabs (price-distribution + sale-to-list charts).
        if (tab === 'long-term') {
            if (this.longTerm.status === 'loaded')
                return;
            await this.fetchLongTerm();
            return;
        }
        if (tab === 'overview') {
            if (this.overview.status === 'loaded')
                return;
            await this.fetchOverview();
            return;
        }
        if (tab === 'velocity') {
            if (this.velocity.status === 'loaded')
                return;
            await this.fetchVelocity();
            return;
        }
        if (tab === 'pricing') {
            // Pricing renders elements from BOTH overview (price-distribution +
            // sale-to-list histogram) and the new pricing payload (bedroom grid +
            // sale-to-list trend). Fetch both in parallel.
            const tasks = [];
            if (this.overview.status !== 'loaded')
                tasks.push(this.fetchOverview());
            if (this.pricing.status !== 'loaded')
                tasks.push(this.fetchPricing());
            if (tasks.length > 0)
                await Promise.all(tasks);
            return;
        }
        if (tab === 'str') {
            const tasks = [];
            if (this.overview.status !== 'loaded')
                tasks.push(this.fetchOverview());
            if (this.strTab.status !== 'loaded')
                tasks.push(this.fetchStr());
            if (tasks.length > 0)
                await Promise.all(tasks);
            return;
        }
    }
    async fetchOverview() {
        if (!this.opts.dataApiUrl) {
            this.overview = { status: 'errored', message: 'No API configured' };
            return;
        }
        this.overview = { status: 'loading' };
        this.renderActiveTab();
        const seq = ++this.fetchSeq;
        try {
            const json = await this.fetchJson('/stats/overview');
            if (seq !== this.fetchSeq)
                return;
            this.overview = { status: 'loaded', data: json };
        }
        catch (err) {
            if (seq !== this.fetchSeq)
                return;
            console.error('[MarketStats] overview fetch failed:', err);
            this.overview = { status: 'errored', message: 'Failed to load stats' };
        }
    }
    async fetchVelocity() {
        if (!this.opts.dataApiUrl) {
            this.velocity = { status: 'errored', message: 'No API configured' };
            return;
        }
        this.velocity = { status: 'loading' };
        this.renderActiveTab();
        const seq = ++this.fetchSeq;
        try {
            const json = await this.fetchJson('/stats/velocity');
            if (seq !== this.fetchSeq)
                return;
            this.velocity = { status: 'loaded', data: json };
        }
        catch (err) {
            if (seq !== this.fetchSeq)
                return;
            console.error('[MarketStats] velocity fetch failed:', err);
            this.velocity = { status: 'errored', message: 'Failed to load velocity stats' };
        }
    }
    async fetchPricing() {
        if (!this.opts.dataApiUrl) {
            this.pricing = { status: 'errored', message: 'No API configured' };
            return;
        }
        this.pricing = { status: 'loading' };
        this.renderActiveTab();
        const seq = ++this.fetchSeq;
        try {
            const json = await this.fetchJson('/stats/pricing');
            if (seq !== this.fetchSeq)
                return;
            this.pricing = { status: 'loaded', data: json };
        }
        catch (err) {
            if (seq !== this.fetchSeq)
                return;
            console.error('[MarketStats] pricing fetch failed:', err);
            this.pricing = { status: 'errored', message: 'Failed to load pricing stats' };
        }
    }
    async fetchStr() {
        if (!this.opts.dataApiUrl) {
            this.strTab = { status: 'errored', message: 'No API configured' };
            return;
        }
        this.strTab = { status: 'loading' };
        this.renderActiveTab();
        const seq = ++this.fetchSeq;
        try {
            const json = await this.fetchJson('/stats/str');
            if (seq !== this.fetchSeq)
                return;
            this.strTab = { status: 'loaded', data: json };
        }
        catch (err) {
            if (seq !== this.fetchSeq)
                return;
            console.error('[MarketStats] str fetch failed:', err);
            this.strTab = { status: 'errored', message: 'Failed to load STR stats' };
        }
    }
    async fetchLongTerm() {
        if (!this.opts.dataApiUrl) {
            this.longTerm = { status: 'errored', message: 'No API configured' };
            return;
        }
        this.longTerm = { status: 'loading' };
        this.renderActiveTab();
        const seq = ++this.fetchSeq;
        try {
            // Long-term takes `years=` instead of `window=`. Build the URL by
            // hand rather than reusing fetchJson (which appends window).
            const params = new URLSearchParams();
            if (this.districts.length > 0)
                params.set('districts', this.districts.join(','));
            params.set('years', String(this.longTermYears));
            const base = this.opts.dataApiUrl.replace(/\/+$/, '');
            const resp = await fetch(`${base}/stats/long-term?${params}`);
            if (!resp.ok)
                throw new Error(`HTTP ${resp.status}`);
            const json = await resp.json();
            if (seq !== this.fetchSeq)
                return;
            if (!json.success || !json.data)
                throw new Error(json.error?.message || 'Unknown error');
            this.longTerm = { status: 'loaded', data: json.data };
        }
        catch (err) {
            if (seq !== this.fetchSeq)
                return;
            console.error('[MarketStats] long-term fetch failed:', err);
            this.longTerm = { status: 'errored', message: 'Failed to load long-term stats' };
        }
    }
    // Picking a different horizon (5y/10y/20y) re-fetches the long-term tab.
    setLongTermYears(y) {
        if (y === this.longTermYears)
            return;
        this.longTermYears = y;
        this.longTerm = { status: 'pending' };
        void this.ensureTabLoaded('long-term').then(() => this.renderActiveTab());
    }
    async fetchJson(path) {
        const params = new URLSearchParams();
        if (this.districts.length > 0)
            params.set('districts', this.districts.join(','));
        params.set('window', this.window);
        const base = this.opts.dataApiUrl.replace(/\/+$/, '');
        const resp = await fetch(`${base}${path}?${params}`);
        if (!resp.ok)
            throw new Error(`HTTP ${resp.status}`);
        const json = await resp.json();
        if (!json.success || !json.data)
            throw new Error(json.error?.message || 'Unknown error');
        return json.data;
    }
    // ---------------------------------------------------------------------
    // Shell + tabs
    // ---------------------------------------------------------------------
    renderShell() {
        const root = this.container.querySelector('#ms-root');
        if (!root)
            return;
        const scopeLabel = (this.overview.status === 'loaded' ? this.overview.data.scope.label : null)
            ?? (this.districts.length === 0 ? 'All Maui' : `${this.districts.length} district${this.districts.length === 1 ? '' : 's'}`);
        const closeBtn = this.opts.showClose !== false && this.opts.onClose
            ? `<button class="ms-close" id="ms-close" title="Close">×</button>`
            : '';
        root.innerHTML = `
      <div class="ms-header">
        <div class="ms-title">📊 Market Stats</div>
        <button class="ms-scope-btn" id="ms-scope-btn" title="Change scope">
          <span>📍</span><span class="ms-scope-label">${escapeHtml(scopeLabel)}</span><span>▾</span>
        </button>
        <div class="ms-window-pills" role="group" aria-label="Time window">
          ${WINDOW_OPTIONS.map(opt => `
            <button class="ms-window-pill ${opt.key === this.window ? 'active' : ''}" data-window="${opt.key}">${opt.label}</button>
          `).join('')}
        </div>
        <div class="ms-spacer"></div>
        ${closeBtn}
      </div>
      <div class="ms-tabs" role="tablist">
        ${TAB_OPTIONS.map(opt => `
          <button class="ms-tab ${opt.key === this.activeTab ? 'active' : ''}" data-tab="${opt.key}" role="tab">
            ${opt.label}
          </button>
        `).join('')}
      </div>
      <select class="ms-tabs-select" id="ms-tabs-select" aria-label="Choose tab">
        ${TAB_OPTIONS.map(opt => `
          <option value="${opt.key}" ${opt.key === this.activeTab ? 'selected' : ''}>${opt.label}</option>
        `).join('')}
      </select>
      <div class="ms-body" id="ms-body"></div>
      <div id="ms-disclaimers"></div>
    `;
        this.wireShell();
        // Shown once per shell render (scope/window change or initial mount),
        // not per-tab - every tab here is MLS-derived aggregate data, so one
        // disclaimer covers the whole panel. Aggregate-data disclaimer replaces
        // idx as the always-visible one (see NeighborhoodModal's matching
        // comment); mls/general/ai stay behind the toggle.
        const disclaimersContainer = root.querySelector('#ms-disclaimers');
        if (disclaimersContainer) {
            this.disclaimers?.destroy();
            this.disclaimers = new Disclaimers({
                container: disclaimersContainer,
                showIdx: false,
                showAggregate: true,
            });
            this.disclaimers.render();
        }
    }
    wireShell() {
        const root = this.container.querySelector('#ms-root');
        if (!root)
            return;
        root.querySelector('#ms-close')?.addEventListener('click', () => {
            this.opts.onClose?.();
        });
        root.querySelectorAll('.ms-window-pill').forEach(btn => {
            btn.addEventListener('click', () => {
                const w = btn.dataset.window;
                if (w !== this.window)
                    this.setWindow(w);
            });
        });
        root.querySelectorAll('.ms-tab').forEach(btn => {
            btn.addEventListener('click', () => {
                const t = btn.dataset.tab;
                if (t !== this.activeTab)
                    this.setTab(t);
            });
        });
        // Mobile dropdown variant (shown via CSS @media query)
        const selectEl = root.querySelector('#ms-tabs-select');
        selectEl?.addEventListener('change', () => {
            const t = selectEl.value;
            if (t !== this.activeTab)
                this.setTab(t);
        });
        root.querySelector('#ms-scope-btn')?.addEventListener('click', () => {
            void this.openScopePicker();
        });
    }
    // ---------------------------------------------------------------------
    // Per-tab body rendering
    // ---------------------------------------------------------------------
    renderActiveTab() {
        const body = this.container.querySelector('#ms-body');
        if (!body)
            return;
        switch (this.activeTab) {
            case 'overview':
                body.innerHTML = this.renderOverview();
                break;
            case 'velocity':
                body.innerHTML = this.renderVelocity();
                break;
            case 'pricing':
                body.innerHTML = this.renderPricing();
                break;
            case 'str':
                body.innerHTML = this.renderStr();
                break;
            case 'long-term':
                body.innerHTML = this.renderLongTerm();
                break;
        }
        // Wire interactivity that needs DOM presence — currently just hover
        // tooltips on all chart cards in the active tab.
        this.attachChartInteractions();
    }
    // -------- Overview tab ----------------------------------------------------
    renderOverview() {
        if (this.overview.status === 'loading')
            return loadingHtml();
        if (this.overview.status === 'errored')
            return errorHtml(this.overview.message);
        if (this.overview.status === 'pending')
            return loadingHtml();
        const d = this.overview.data;
        const narrative = this.buildNarrative(d);
        const tiles = this.buildHeadlineTiles(d);
        const coverage = renderCoverageBanner(d.coverage);
        // Use the full interactive chart in Overview. /stats/overview returns
        // 12 buckets (vs 24 from /stats/velocity); we compute the bucket size
        // from the window, the chart renders identically with hover tooltips.
        const bucketDays = d.absorption.weeks.length > 0
            ? Math.max(1, Math.round(d.window.days / d.absorption.weeks.length))
            : d.window.days;
        return `
      ${coverage}
      ${narrative}
      ${tiles}
      <div class="ms-chart-card" data-chart="absorption">
        <h4 class="ms-chart-title">Absorption — new listings vs sold</h4>
        <p class="ms-chart-subtitle">${bucketDays}-day buckets across ${d.window.days} days. Hover for details.</p>
        ${renderAbsorptionFull({ weeks: d.absorption.weeks, bucketDays })}
      </div>
    `;
    }
    // Composes a single-sentence "what's happening" headline. Bias toward
    // absorption (the most decisive momentum signal) when sample sizes allow,
    // falling back to inventory + price snapshot otherwise.
    buildNarrative(d) {
        const { absorptionRate, priorAbsorptionRate, soldInWindow } = d.headline;
        let tone = '';
        let body = '';
        if (absorptionRate != null && priorAbsorptionRate != null && soldInWindow >= 5) {
            const pct = ((absorptionRate - priorAbsorptionRate) / Math.abs(priorAbsorptionRate)) * 100;
            if (Math.abs(pct) >= 5) {
                tone = pct < 0 ? 'cool' : 'warm';
                const dir = pct < 0 ? 'cooling' : 'warming';
                body = `Market is <strong>${dir}</strong> in ${escapeHtml(d.scope.label)}: absorption is <strong>${absorptionRate.toFixed(2)}</strong>, ${pct < 0 ? 'down' : 'up'} ${Math.abs(pct).toFixed(0)}% vs the prior ${d.window.days} days.`;
            }
            else {
                body = `Market is <strong>steady</strong> in ${escapeHtml(d.scope.label)}: absorption ${absorptionRate.toFixed(2)}, roughly unchanged vs the prior ${d.window.days} days.`;
            }
        }
        else if (d.headline.medianListPrice != null) {
            body = `<strong>${d.headline.activeCount}</strong> active listings in ${escapeHtml(d.scope.label)}, median list <strong>${formatPrice(d.headline.medianListPrice)}</strong>${d.headline.medianPsf ? ` (${formatPsf(d.headline.medianPsf)}/sqft)` : ''}.`;
        }
        else {
            body = `<strong>${d.headline.activeCount}</strong> active listings in ${escapeHtml(d.scope.label)}.`;
        }
        return `<div class="ms-narrative ${tone}">${body}</div>`;
    }
    buildHeadlineTiles(d) {
        const h = d.headline;
        const tiles = [
            tile('Active', formatCount(h.activeCount), delta(h.activeCount, h.priorActiveCount, false)),
            tile('Median list', formatPrice(h.medianListPrice), delta(h.medianListPrice, h.priorMedianListPrice, false)),
            tile('Median $/sf', formatPsf(h.medianPsf), delta(h.medianPsf, h.priorMedianPsf, false)),
            tile('Absorption', formatRatio(h.absorptionRate), delta(h.absorptionRate, h.priorAbsorptionRate, true)),
            tile('Sale/list', formatPercent(h.saleToListMedian), delta(h.saleToListMedian, h.priorSaleToListMedian, false)),
        ].join('');
        return `<div class="ms-tiles">${tiles}</div>`;
    }
    // -------- Velocity tab ----------------------------------------------------
    renderVelocity() {
        if (this.velocity.status === 'loading')
            return loadingHtml();
        if (this.velocity.status === 'errored')
            return errorHtml(this.velocity.message);
        if (this.velocity.status === 'pending')
            return loadingHtml();
        const d = this.velocity.data;
        // Absorption now lives in Overview (the higher-traffic tab); Velocity
        // focuses on the harder-to-find momentum signals: DOM distribution,
        // time-to-first-price-cut, and the % of actives that have already cut.
        const cuts = d.priceCuts;
        const coverage = renderCoverageBanner(d.coverage);
        const cutsCard = `
      <div class="ms-bignum-card">
        <div class="ms-bignum">${cuts.pctReduced != null ? cuts.pctReduced.toFixed(0) + '%' : '—'}</div>
        <div class="ms-bignum-label">of active listings have had a price cut</div>
        <div class="ms-bignum-detail">${cuts.withReductions} of ${cuts.activeCount} active</div>
      </div>
    `;
        return `
      ${coverage}
      ${cutsCard}
      <div class="ms-chart-card" data-chart="dom">
        <h4 class="ms-chart-title">Days on market — sold listings</h4>
        <p class="ms-chart-subtitle">How long did closed deals sit before they sold?</p>
        ${renderBucketedHistogram(d.dom, { unit: 'd', emptyMsg: 'No sold listings in window', tooltipPrefix: 'Sold in' })}
      </div>
      <div class="ms-chart-card" data-chart="ttfc">
        <h4 class="ms-chart-title">Time to first price cut</h4>
        <p class="ms-chart-subtitle">For listings that had ≥1 reduction, how soon did the seller move?</p>
        ${renderBucketedHistogram(d.timeToFirstPriceChange, { unit: 'd', emptyMsg: 'No price changes in scope', tooltipPrefix: 'First cut at' })}
      </div>
    `;
    }
    // -------- Pricing tab ----------------------------------------------------
    renderPricing() {
        if (this.overview.status === 'loading' || this.pricing.status === 'loading')
            return loadingHtml();
        if (this.overview.status === 'errored')
            return errorHtml(this.overview.message);
        if (this.pricing.status === 'errored')
            return errorHtml(this.pricing.message);
        if (this.overview.status !== 'loaded' || this.pricing.status !== 'loaded')
            return loadingHtml();
        const d = this.overview.data;
        const p = this.pricing.data;
        return `
      ${renderCoverageBanner(d.coverage)}
      <div class="ms-chart-card" data-chart="price-distribution">
        <h4 class="ms-chart-title">Active inventory by price band</h4>
        <p class="ms-chart-subtitle">${escapeHtml(d.scope.label)} · ${d.headline.activeCount} active listings</p>
        ${renderPriceDistribution(d.priceDistribution)}
      </div>
      <div class="ms-chart-card" data-chart="bedroom-grid">
        <h4 class="ms-chart-title">Median price by bedroom count</h4>
        <p class="ms-chart-subtitle">Active listings · median list and median $/sqft per bedroom</p>
        ${renderBedroomGrid(p.bedroomGrid)}
      </div>
      <div class="ms-chart-card" data-chart="sale-to-list-trend">
        <h4 class="ms-chart-title">Sale-to-list ratio over time</h4>
        <p class="ms-chart-subtitle">Monthly average for the last 12 months</p>
        ${renderSaleToListTrend(p.saleToListTrend)}
      </div>
      <div class="ms-chart-card" data-chart="sale-to-list">
        <h4 class="ms-chart-title">Sale-to-list ratio — distribution</h4>
        <p class="ms-chart-subtitle">How close did closed sales land to ask?</p>
        ${renderSaleToListHistogram(d.saleToList)}
      </div>
    `;
    }
    // -------- STR tab --------------------------------------------------------
    renderStr() {
        if (this.overview.status === 'loading' || this.strTab.status === 'loading')
            return loadingHtml();
        if (this.overview.status === 'errored')
            return errorHtml(this.overview.message);
        if (this.strTab.status === 'errored')
            return errorHtml(this.strTab.message);
        if (this.overview.status !== 'loaded' || this.strTab.status !== 'loaded')
            return loadingHtml();
        const d = this.overview.data;
        const s = this.strTab.data;
        // Per-district breakdown only meaningful when scope spans 2+ districts;
        // for a single district it'd just repeat the headline chart.
        const perDistrictHtml = s.perDistrict.rows.length >= 2
            ? `
        <div class="ms-chart-card" data-chart="str-per-district">
          <h4 class="ms-chart-title">STR premium by district</h4>
          <p class="ms-chart-subtitle">$/sqft of STR-eligible vs non-STR active condos</p>
          ${renderStrPerDistrict(s.perDistrict)}
        </div>
      `
            : '';
        return `
      ${renderCoverageBanner(d.coverage)}
      <div class="ms-chart-card" data-chart="str-premium">
        <h4 class="ms-chart-title">STR-eligible vs non-STR — $/sqft</h4>
        <p class="ms-chart-subtitle">Active condos in ${escapeHtml(d.scope.label)}</p>
        ${renderStrPremium(d.strPremium)}
      </div>
      ${perDistrictHtml}
      <div class="ms-chart-card" data-chart="str-comparison">
        <h4 class="ms-chart-title">How STR units sell — vs non-STR</h4>
        <p class="ms-chart-subtitle">Closed sales in window — does STR-eligible take longer or close closer to ask?</p>
        ${renderStrComparison(s.comparison)}
      </div>
    `;
    }
    // -------- Long-term tab (placeholder) -----------------------------------
    renderLongTerm() {
        if (this.longTerm.status === 'loading' || this.longTerm.status === 'pending')
            return loadingHtml();
        if (this.longTerm.status === 'errored')
            return errorHtml(this.longTerm.message);
        const d = this.longTerm.data;
        // Years selector — local to this tab. 5 / 10 / 20 horizons drive a
        // re-fetch when changed.
        const horizonPicker = `
      <div class="ms-window-pills" role="group" aria-label="Time horizon" style="margin-bottom: 4px;">
        ${[5, 10, 20].map(y => `
          <button class="ms-window-pill ${y === this.longTermYears ? 'active' : ''}" data-horizon="${y}" style="background: ${y === this.longTermYears ? '#2c5530' : '#f3f4f6'}; color: ${y === this.longTermYears ? 'white' : '#6b7280'}; border-color: ${y === this.longTermYears ? '#2c5530' : '#e5e7eb'};">
            ${y} years
          </button>
        `).join('')}
      </div>
    `;
        // Headline numbers from the data
        const priceYears = d.annualPrice.years.filter(y => y.median != null);
        const priceChange = priceYears.length >= 2
            ? ((priceYears[priceYears.length - 1].median - priceYears[0].median) / priceYears[0].median) * 100
            : null;
        const totalSales = d.annualVolume.years.reduce((s, y) => s + y.count, 0);
        const peakYear = d.annualVolume.years.reduce((best, y) => !best || y.count > best.count ? y : best, null);
        const summaryRow = `
      <div class="cc-cmp-grid">
        <div class="cc-cmp-metric">
          <div class="cc-cmp-title">Median price change</div>
          <div class="cc-cmp-value" style="color: ${priceChange != null && priceChange >= 0 ? '#059669' : '#dc2626'};">
            ${priceChange != null ? (priceChange >= 0 ? '+' : '') + priceChange.toFixed(0) + '%' : '—'}
          </div>
          <div class="cc-cmp-sample">over ${this.longTermYears} years</div>
        </div>
        <div class="cc-cmp-metric">
          <div class="cc-cmp-title">Total sales</div>
          <div class="cc-cmp-value">${totalSales.toLocaleString()}</div>
          <div class="cc-cmp-sample">peak: ${peakYear ? peakYear.count.toLocaleString() + ' in ' + peakYear.year : '—'}</div>
        </div>
        <div class="cc-cmp-metric">
          <div class="cc-cmp-title">Median holding period</div>
          <div class="cc-cmp-value">${d.holdingPeriod.medianYears != null ? d.holdingPeriod.medianYears + ' yrs' : '—'}</div>
          <div class="cc-cmp-sample">${d.holdingPeriod.sample} repeat-sale samples</div>
        </div>
      </div>
    `;
        return `
      ${horizonPicker}
      ${summaryRow}
      <div class="ms-chart-card" data-chart="annual-price">
        <h4 class="ms-chart-title">Median sale price by year</h4>
        <p class="ms-chart-subtitle">From assessor data — the ${this.longTermYears}-year trend in ${escapeHtml(d.scope.label)}</p>
        ${renderAnnualPriceTrend(d.annualPrice)}
      </div>
      <div class="ms-chart-card" data-chart="annual-volume">
        <h4 class="ms-chart-title">Sales volume by year</h4>
        <p class="ms-chart-subtitle">How active was the market each year?</p>
        ${renderAnnualVolume(d.annualVolume)}
      </div>
      <div class="ms-chart-card" data-chart="holding-period">
        <h4 class="ms-chart-title">Holding period — how long do owners hold?</h4>
        <p class="ms-chart-subtitle">For TMKs that sold ≥2 times in the last ${this.longTermYears} years, the gap between sales</p>
        ${renderHoldingPeriod(d.holdingPeriod)}
      </div>
      <p style="font-size: 12px; color: #9ca3af;">
        Note: $/sqft trend will surface here once the assessor's interior-area data is populated.
        Currently <code>tmk_records.total_interior_area</code> is empty across the board.
      </p>
    `;
    }
    // ---------------------------------------------------------------------
    // Chart interactivity
    // ---------------------------------------------------------------------
    attachChartInteractions() {
        const body = this.container.querySelector('#ms-body');
        if (!body)
            return;
        body.querySelectorAll('.ms-chart-card').forEach(card => {
            attachHoverTooltip(card, {
                onClick: (hit) => this.handleChartClick(card, hit),
            });
        });
        // Long-term tab horizon picker (only present when activeTab === 'long-term')
        body.querySelectorAll('[data-horizon]').forEach(btn => {
            btn.addEventListener('click', () => {
                const y = parseInt(btn.dataset.horizon || '5', 10);
                this.setLongTermYears(y);
            });
        });
    }
    // Chart click → onDrillToListings. The hit element may carry a
    // `data-drill` JSON payload (e.g., price band → priceMin/priceMax) which
    // we merge into the DrillFilter. Charts without a per-bar drill (e.g.,
    // DOM histogram — there's no listing-filter equivalent for "DOM band")
    // still fire a click; the host typically just closes the modal.
    handleChartClick(card, hit) {
        let drill = {};
        const drillJson = hit.getAttribute('data-drill');
        if (drillJson) {
            try {
                drill = JSON.parse(drillJson);
            }
            catch { /* ignore malformed */ }
        }
        const filter = {
            districts: [...this.districts],
            label: drill.label || card.dataset.chart || 'stats-drill',
            ...drill,
        };
        if (this.opts.onDrillToListings) {
            this.opts.onDrillToListings(filter);
        }
        else {
            this.opts.onClose?.();
        }
    }
    // ---------------------------------------------------------------------
    // Scope picker (overlay inside the component root)
    // ---------------------------------------------------------------------
    async openScopePicker() {
        let catalog = this.opts.districtCatalog;
        if (!catalog && this.opts.dataApiUrl) {
            try {
                const base = this.opts.dataApiUrl.replace(/\/+$/, '');
                const resp = await fetch(`${base}/districts`);
                const json = await resp.json();
                catalog = (json.data || []).map(d => ({
                    id: d.id,
                    name: d.name,
                    regionId: d.regionId ?? d.region_id,
                    regionName: d.regionName ?? d.region_name,
                }));
            }
            catch {
                catalog = [];
            }
        }
        catalog = catalog ?? [];
        const root = this.container.querySelector('#ms-root');
        if (!root)
            return;
        const byRegion = new Map();
        const regionNames = new Map();
        for (const d of catalog) {
            const regionId = d.regionId ?? '_none';
            regionNames.set(regionId, d.regionName ?? 'Other');
            if (!byRegion.has(regionId))
                byRegion.set(regionId, []);
            byRegion.get(regionId).push({ id: d.id, name: d.name });
        }
        const initialSet = new Set(this.districts);
        const pickerRegions = Array.from(byRegion.entries()).map(([rid, dlist]) => `
      <h4>${escapeHtml(regionNames.get(rid) || 'Other')}</h4>
      <button type="button" class="ms-picker-option" data-action="region" data-ids="${dlist.map(d => d.id).join(',')}">
        Whole region: ${escapeHtml(regionNames.get(rid) || 'Other')}
      </button>
      ${dlist.map(d => `
        <button type="button" class="ms-picker-option ${initialSet.has(d.id) ? 'selected' : ''}" data-id="${escapeHtml(d.id)}">
          ${escapeHtml(d.name)}
        </button>
      `).join('')}
    `).join('');
        const overlay = document.createElement('div');
        overlay.className = 'ms-picker-backdrop';
        overlay.innerHTML = `
      <div class="ms-picker" role="dialog" aria-label="Choose scope">
        <button type="button" class="ms-picker-option ${this.districts.length === 0 ? 'selected' : ''}" data-action="all">
          All Maui
        </button>
        ${pickerRegions}
        <div class="ms-picker-actions">
          <button type="button" data-action="cancel">Cancel</button>
        </div>
      </div>
    `;
        root.style.position = 'relative';
        root.appendChild(overlay);
        const close = () => overlay.remove();
        overlay.addEventListener('click', (e) => {
            if (e.target === overlay)
                close();
        });
        overlay.querySelector('[data-action="cancel"]')?.addEventListener('click', close);
        overlay.querySelector('[data-action="all"]')?.addEventListener('click', () => {
            close();
            this.setScope([]);
        });
        overlay.querySelectorAll('[data-action="region"]').forEach(btn => {
            btn.addEventListener('click', () => {
                close();
                const ids = (btn.dataset.ids || '').split(',').filter(Boolean);
                this.setScope(ids);
            });
        });
        overlay.querySelectorAll('.ms-picker-option[data-id]').forEach(btn => {
            btn.addEventListener('click', () => {
                close();
                const id = btn.dataset.id;
                this.setScope([id]);
            });
        });
    }
}
// ---------------------------------------------------------------------------
// Tile + delta formatting helpers
// ---------------------------------------------------------------------------
function tile(label, value, deltaHtml) {
    return `
    <div class="ms-tile">
      <div class="ms-tile-label">${escapeHtml(label)}</div>
      <div class="ms-tile-value">${escapeHtml(value)}</div>
      ${deltaHtml}
    </div>
  `;
}
// Exported (unlike tile() above, which is Overview-tab-specific markup) -
// GeographyModal's Market Data tab reuses this same up/down/flat + color-
// inversion logic and these formatters for its own headline stat strip,
// rather than reimplementing them. See GeographyModal.ts's
// buildMarketHeadline().
export function delta(current, prior, invertColor) {
    if (current == null || prior == null || prior === 0) {
        return `<div class="ms-tile-delta flat">—</div>`;
    }
    const diffPct = ((current - prior) / Math.abs(prior)) * 100;
    if (Math.abs(diffPct) < 0.5) {
        return `<div class="ms-tile-delta flat">flat vs prior</div>`;
    }
    const up = diffPct > 0;
    // For some metrics (absorption rate) higher = market cooling, so invert
    // the up/down color semantics. The arrow direction stays literal.
    const cls = invertColor ? (up ? 'down' : 'up') : (up ? 'up' : 'down');
    const arrow = up ? '▲' : '▼';
    const sign = up ? '+' : '';
    return `<div class="ms-tile-delta ${cls}">${arrow} ${sign}${diffPct.toFixed(0)}% vs prior</div>`;
}
export function formatCount(n) {
    if (n == null)
        return '—';
    return n.toLocaleString();
}
export function formatPrice(n) {
    if (n == null)
        return '—';
    if (n >= 1_000_000)
        return '$' + (n / 1_000_000).toFixed(2) + 'M';
    if (n >= 1_000)
        return '$' + (n / 1_000).toFixed(0) + 'K';
    return '$' + n.toFixed(0);
}
export function formatPsf(n) {
    if (n == null)
        return '—';
    return '$' + Math.round(n).toLocaleString();
}
export function formatRatio(n) {
    if (n == null)
        return '—';
    return n.toFixed(2);
}
export function formatPercent(n) {
    if (n == null)
        return '—';
    return (n * 100).toFixed(1) + '%';
}
function loadingHtml() {
    return `<div class="ms-loading">Loading…</div>`;
}
function errorHtml(msg) {
    return `<div class="ms-error">${escapeHtml(msg)}</div>`;
}
// Shows a small amber banner when MLS history is shallower than the
// requested window. Returns an empty string for full coverage so callers
// can include it unconditionally.
function renderCoverageBanner(c) {
    if (!c || !c.isPartial)
        return '';
    if (!c.earliestData) {
        return `<div class="ms-coverage-banner">⚠ No MLS history available yet for this scope. Charts will populate as data comes in.</div>`;
    }
    const earliestPretty = new Date(c.earliestData + 'T00:00:00Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `
    <div class="ms-coverage-banner">
      ⚠ MLS history is shallow: showing <strong>${c.coveredDays} of the ${c.windowDays} days</strong> requested
      (data starts ${escapeHtml(earliestPretty)}). Older comparisons (vs prior period) won't be available until the history fills in.
    </div>
  `;
}
function escapeHtml(s) {
    if (s == null)
        return '';
    return String(s).replace(/[&<>"']/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));
}
//# sourceMappingURL=MarketStats.js.map